import { useCallback, useEffect, useState } from 'react';

export type EntityKind = 'league' | 'user' | 'squad' | 'community' | 'item' | 'player';

export interface RecentEntity {
  kind: EntityKind;
  id: string;
  name: string;
  to: string;
  viewedAt: number;
}

const STORAGE_KEY = 'mesh-admin.recents';
const MAX_RECENTS = 8;
/** Fired on write so every mounted consumer updates, which `storage` alone won't do. */
const CHANGE_EVENT = 'mesh-admin:recents-changed';

/** Two-letter tag and tint per entity kind, used by the sidebar and the palette. */
export const ENTITY_KIND_STYLE: Record<
  EntityKind,
  { tag: string; tint: string; tintFg: string; label: string }
> = {
  league: {
    tag: 'LG',
    tint: 'rgba(var(--brand-rgb), 0.22)',
    tintFg: 'var(--brand-soft)',
    label: 'League',
  },
  user: {
    tag: 'US',
    tint: 'rgba(var(--info-rgb), 0.2)',
    tintFg: 'var(--info-soft)',
    label: 'User',
  },
  squad: {
    tag: 'SQ',
    tint: 'rgba(var(--ok-rgb), 0.18)',
    tintFg: 'var(--ok)',
    label: 'Squad',
  },
  community: {
    tag: 'CM',
    tint: 'rgba(var(--epic-rgb), 0.2)',
    tintFg: 'var(--epic)',
    label: 'Community',
  },
  item: {
    tag: 'IT',
    tint: 'rgba(var(--gold-rgb), 0.18)',
    tintFg: 'var(--gold)',
    label: 'Shop item',
  },
  player: {
    tag: 'PL',
    tint: 'rgba(var(--tx3-rgb), 0.24)',
    tintFg: 'var(--tx2)',
    label: 'Player',
  },
};

function read(): RecentEntity[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is RecentEntity =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as RecentEntity).id === 'string' &&
        typeof (entry as RecentEntity).to === 'string',
    );
  } catch {
    return [];
  }
}

/**
 * Record a visit. Called from entity detail routes so an operator can jump back
 * to the last few leagues/users they were looking at — the brief's "recent
 * entities" affordance, which matters most mid-incident.
 */
export function recordRecentEntity(entity: Omit<RecentEntity, 'viewedAt'>): void {
  try {
    const next = [
      { ...entity, viewedAt: Date.now() },
      ...read().filter((item) => !(item.kind === entity.kind && item.id === entity.id)),
    ].slice(0, MAX_RECENTS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // Recents are a convenience; never let them break navigation.
  }
}

export function useRecentEntities(): RecentEntity[] {
  const [recents, setRecents] = useState<RecentEntity[]>(read);

  const refresh = useCallback(() => setRecents(read()), []);

  useEffect(() => {
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  return recents;
}
